(() => {
  'use strict';

  // Loaded only on the homepage by theme.liquid. No section IDs, URLs or task IDs
  // are configured here: Liquid and the real links are the content source.
  const mainContent = document.getElementById('MainContent');
  if (!mainContent || window.Shopify?.designMode) return;
  if (window.__lexiboHomepageTrackingInitialized) return;
  window.__lexiboHomepageTrackingInitialized = true;

  const contract = Object.freeze({
    contractVersion: '2.0',
    measurementId: 'G-SP7RV0ZSQ6',
    viewEvent: 'homepage_view',
    clickEvent: 'homepage_path_click',
  });
  const developmentThemeRoles = new Set(['development', 'unpublished']);
  const handledClickEvents = new WeakSet();
  const seenEventIds = new Set();
  const pageInstance = createPageInstance();
  let consentState = 'unknown';
  let clickIndex = 0;
  let viewSent = false;

  window.dataLayer = window.dataLayer || [];
  window.__lexiboHomepageTrackingContract = contract;

  function createPageInstance() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID().replace(/-/g, '').slice(0, 16);
    }
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  }

  const normalizeText = (value, maxLength = 100) =>
    String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, maxLength);

  const findExistingGoogleTag = () =>
    document.querySelector('script[src*="googletagmanager.com/gtag/js"]');

  const ensureGtagFunction = () => {
    window.gtag =
      window.gtag ||
      function () {
        window.dataLayer.push(arguments);
      };
  };

  const appendFallbackGoogleTag = () => {
    if (consentState !== 'granted' || findExistingGoogleTag()) return;

    const script = document.createElement('script');
    script.async = true;
    script.src =
      'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(contract.measurementId);
    script.dataset.lexiboHomepageGa4 = '';
    document.head.appendChild(script);
  };

  const initializeGoogleTag = () => {
    ensureGtagFunction();

    if (!window.__lexiboHomepageGoogleTagConfigured) {
      window.__lexiboHomepageGoogleTagConfigured = true;
      window.gtag('js', new Date());
      window.gtag('config', contract.measurementId, {
        send_page_view: false,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
      });
    }

    appendFallbackGoogleTag();
  };

  const updateGoogleConsent = (state) => {
    ensureGtagFunction();
    window.gtag('consent', 'update', {
      analytics_storage: state,
    });
  };

  const sendToGa4 = (payload) => {
    if (consentState !== 'granted' || seenEventIds.has(payload.event_id)) return;

    seenEventIds.add(payload.event_id);
    initializeGoogleTag();
    const { event, ...parameters } = payload;
    const themeRole = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;

    window.gtag('event', event, {
      ...parameters,
      send_to: contract.measurementId,
      transport_type: 'beacon',
      ...(developmentThemeRoles.has(themeRole) ? { debug_mode: true } : {}),
    });
  };

  const sendHomepageView = () => {
    if (viewSent || consentState !== 'granted') return;
    viewSent = true;
    sendToGa4({
      event: contract.viewEvent,
      event_id: 'hpv:' + pageInstance,
      tracking_version: contract.contractVersion,
      page_type: 'index',
      destination_url: window.location.origin + window.location.pathname,
    });
  };

  const grantAnalytics = () => {
    if (consentState === 'granted') return;
    consentState = 'granted';
    updateGoogleConsent('granted');
    initializeGoogleTag();
    sendHomepageView();
  };

  const denyAnalytics = () => {
    consentState = 'denied';
    if (window.__lexiboHomepageGoogleTagConfigured) updateGoogleConsent('denied');
  };

  const updateAnalyticsConsent = (explicitChoice = false) => {
    const customerPrivacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!customerPrivacy || typeof customerPrivacy.analyticsProcessingAllowed !== 'function') return;

    if (customerPrivacy.analyticsProcessingAllowed()) {
      grantAnalytics();
    } else if (explicitChoice || consentState === 'granted') {
      denyAnalytics();
    }
  };

  const initializePrivacy = () => {
    if (window.Shopify && window.Shopify.customerPrivacy) {
      updateAnalyticsConsent(false);
      return;
    }

    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (!error) updateAnalyticsConsent(false);
    });
  };

  const getSectionElement = (link) => link.closest('[id^="shopify-section-"]');

  const getSectionKey = (sectionElement) => {
    if (!sectionElement || !sectionElement.id) return '';
    const separatorIndex = sectionElement.id.lastIndexOf('__');
    return separatorIndex >= 0 ? sectionElement.id.slice(separatorIndex + 2) : sectionElement.id;
  };

  const getNormalizedSegments = (url) => {
    const segments = url.pathname.split('/').filter(Boolean);
    if (segments[0] && /^[a-z]{2}(?:-[a-z]{2})?$/i.test(segments[0])) segments.shift();
    return segments;
  };

  const getNormalizedPath = (url) => {
    const segments = getNormalizedSegments(url);
    return '/' + segments.join('/');
  };

  const decodeHandle = (value) => {
    try {
      return decodeURIComponent(value);
    } catch (_error) {
      return value;
    }
  };

  const classifyDestination = (url) => {
    const segments = getNormalizedSegments(url);
    // Shopify may link a product through a collection-scoped URL.
    const productIndex = segments[0] === 'collections' && segments[2] === 'products' ? 2 : 0;
    const routeTypes = { collections: 'collection', products: 'product', pages: 'page', blogs: 'blog', search: 'search', cart: 'cart' };
    return {
      type: routeTypes[segments[productIndex]] || 'internal',
      handle: normalizeText(decodeHandle(segments[productIndex + 1] || ''), 100),
    };
  };

  const getLinkText = (link) =>
    normalizeText(
      link.getAttribute('aria-label') ||
        link.querySelector('img[alt]')?.getAttribute('alt') ||
        link.getAttribute('title') ||
        link.textContent,
      100
    );

  const getItemPosition = (sectionElement, link) => {
    if (!sectionElement) return 0;
    const destinations = [];

    sectionElement.querySelectorAll('a[href]').forEach((candidate) => {
      let candidateUrl;
      try {
        candidateUrl = new URL(candidate.href, window.location.href);
      } catch (_error) {
        return;
      }
      if (candidateUrl.origin !== window.location.origin) return;
      const path = getNormalizedPath(candidateUrl);
      if (!destinations.includes(path)) destinations.push(path);
    });

    let currentUrl;
    try {
      currentUrl = new URL(link.href, window.location.href);
    } catch (_error) {
      return 0;
    }
    const index = destinations.indexOf(getNormalizedPath(currentUrl));
    return index >= 0 ? index + 1 : 0;
  };

  const buildClickPayload = (link) => {
    const url = new URL(link.href, window.location.href);
    const normalizedPath = getNormalizedPath(url);
    const sectionElement = getSectionElement(link);
    const componentElement = link.closest('[data-track-component]');
    const component = componentElement?.dataset.trackComponent || 'homepage_content';
    const sectionId = componentElement?.dataset.trackSection || getSectionKey(sectionElement);
    const positionElement = link.closest('[data-track-position]');
    const productElement = link.closest('[data-track-product-id]');
    const destination = classifyDestination(url);
    const declaredPosition = Number(positionElement?.dataset.trackPosition);
    clickIndex += 1;

    return {
      event: contract.clickEvent,
      event_id: 'hpc:' + pageInstance + ':' + clickIndex,
      tracking_version: contract.contractVersion,
      page_type: 'index',
      component,
      section_id: normalizeText(sectionId, 100),
      element_position: Number.isInteger(declaredPosition) && declaredPosition > 0
        ? declaredPosition : getItemPosition(sectionElement, link),
      destination_type: destination.type,
      destination_handle: destination.handle,
      // Never send query strings (search terms/tokens) or fragment values.
      destination_url: window.location.origin + normalizedPath,
      link_text: getLinkText(link),
      ...(componentElement?.dataset.trackCollection
        ? { collection_handle: normalizeText(componentElement.dataset.trackCollection, 100) } : {}),
      ...(destination.type === 'product' ? {
        product_handle: destination.handle,
        ...(productElement?.dataset.trackProductId
          ? { product_id: normalizeText(productElement.dataset.trackProductId, 30) } : {}),
      } : {}),
    };
  };

  const shouldTrackLink = (link) => {
    if (!link || !link.href || link.hasAttribute('download')) return false;
    if (link.matches('[data-home-tracking-ignore], [aria-disabled="true"]')) return false;

    let url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (_error) {
      return false;
    }

    if (!['http:', 'https:'].includes(url.protocol) || url.origin !== window.location.origin) return false;
    return !(
      getNormalizedPath(url) === getNormalizedPath(window.location) &&
      url.search === window.location.search &&
      url.hash
    );
  };

  document.addEventListener('click', (event) => {
    if (handledClickEvents.has(event)) return;
    handledClickEvents.add(event);

    const target = event.target instanceof Element ? event.target : event.target && event.target.parentElement;
    const link = target && target.closest('a[href]');
    if (!link || !mainContent.contains(link) || !shouldTrackLink(link)) return;
    if (consentState !== 'granted') return;
    sendToGa4(buildClickPayload(link));
  });

  document.addEventListener('visitorConsentCollected', () => updateAnalyticsConsent(true));
  initializePrivacy();
})();
